// Pure prop-builders for the document library's layout components. Kept outside the page's
// hook/component so each stays a small, independently readable function.

const mappingPath = (universityId, fileId) =>
  `/admin/university/${universityId}/documents/${fileId}/mapping`;

/** File-row action handlers shared by the file table; bundled so callers can spread one prop. */
export const buildFileHandlers = ({
  navigate,
  universityId,
  canEdit,
  canDelete,
  canSend,
  fileMutation,
  deleteFileMutation,
  onSend,
  onConfirmDelete,
}) => ({
  onOpenMapping: (file) => navigate(mappingPath(universityId, file.id)),
  onRename: (file) => {
    const name = window.prompt("New name", file.name);
    if (name) fileMutation.mutate({ fileId: file.id, payload: { name } });
  },
  onMove: (file) => {
    const folderId = window.prompt("Move to folder id", String(file.folderId));
    if (folderId) fileMutation.mutate({ fileId: file.id, payload: { folderId: Number(folderId) } });
  },
  onToggleActive: (file) =>
    fileMutation.mutate({ fileId: file.id, payload: { active: file.active === false } }),
  onDelete: (file) =>
    onConfirmDelete({
      kind: "document",
      name: file.name,
      detail: "Its field mapping goes with it",
      run: () => deleteFileMutation.mutate(file.id),
    }),
  onSend,
  canEdit,
  canDelete,
  canSend,
});

/** Props for the left-hand `FolderTree` pane. */
export const buildFolderTreeProps = ({
  folders,
  selectedFolderId,
  setSelectedFolderId,
  setFolderDialog,
  deleteFolderMutation,
  onConfirmDelete,
  canCreate,
  canEdit,
  canDelete,
}) => ({
  folders,
  selectedId: selectedFolderId,
  onSelect: setSelectedFolderId,
  onCreate: (parentId) => setFolderDialog({ mode: "create", parentId, name: "" }),
  onRename: (folder) => setFolderDialog({ mode: "rename", folder, name: folder.name }),
  onDelete: (folder) =>
    onConfirmDelete({
      kind: "folder",
      name: folder.name,
      run: () => deleteFolderMutation.mutate(folder.id),
    }),
  canCreate,
  canEdit,
  canDelete,
});

/** Props for the title/new-folder action and the search box. */
export const buildToolbarProps = ({
  canCreate,
  setFolderDialog,
  query,
  setQuery,
  setActiveSearch,
  clearSearch,
  activeSearch,
}) => ({
  canCreate,
  onNewFolder: () => setFolderDialog({ mode: "create", parentId: null, name: "" }),
  query,
  onQueryChange: setQuery,
  onSearch: () => setActiveSearch(query),
  onClear: clearSearch,
  activeSearch,
});

/** Props for the right-hand `LibraryContent` pane. */
export const buildContentProps = ({
  activeSearch,
  searchQuery,
  openFromSearch,
  breadcrumb,
  selectedFolderId,
  setSelectedFolderId,
  canCreate,
  uploadName,
  setUploadName,
  uploadMutation,
  filesQuery,
  fileHandlers,
}) => ({
  activeSearch,
  searchResult: searchQuery.data,
  onOpenSearchFolder: openFromSearch,
  onOpenSearchFile: (hit) => openFromSearch(hit.folderId),
  breadcrumb,
  selectedFolderId,
  onSelectFolder: setSelectedFolderId,
  canCreate,
  uploadName,
  onUploadNameChange: setUploadName,
  onUpload: (file) => uploadMutation.mutate(file),
  uploading: uploadMutation.isLoading,
  files: filesQuery.data || [],
  fileHandlers,
});

/** Props for the create/rename `FolderDialog`. */
export const buildDialogProps = ({ folderDialog, setFolderDialog, folderMutation }) => ({
  folderDialog,
  onChangeName: (name) => setFolderDialog((current) => ({ ...current, name })),
  onClose: () => setFolderDialog(null),
  onSave: () =>
    folderMutation.mutate({
      folderId: folderDialog.mode === "rename" ? folderDialog.folder.id : null,
      parentId: folderDialog.parentId ?? null,
      name: folderDialog.name.trim(),
    }),
  saving: folderMutation.isLoading,
});
